import { useState, memo } from "react";

interface InlineThinkingProps {
  text: string;
}

const PILL_LABEL = "reasoned";

/**
 * Renders a reasoning/thinking part as the companion's inner monologue, not a
 * debug log. Collapsed: a small quiet pill ("◦ reasoned") above the message.
 * Expanded: the thinking text typeset in the same warm serif italic used for
 * the companion's voice (see .inline-thinking-text), not a utilitarian frame.
 */
export const InlineThinking = memo(function InlineThinking({ text }: InlineThinkingProps) {
  const [expanded, setExpanded] = useState(false);

  // CC CLI redacts thinking text — still show the pill so the user knows the
  // AI thought, but there's no content to expand into.
  const isRedacted = text === "[REDACTED]";

  if (isRedacted) {
    return (
      <div className="inline-thinking">
        <span className="inline-thinking-pill inline-thinking-pill--static">
          <span className="inline-thinking-dot" aria-hidden="true">◦</span> {PILL_LABEL}
        </span>
      </div>
    );
  }

  return (
    <div className={`inline-thinking${expanded ? " inline-thinking--expanded" : ""}`}>
      <button
        type="button"
        className="inline-thinking-pill"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse reasoning" : "Expand reasoning"}
      >
        <span className="inline-thinking-dot" aria-hidden="true">◦</span> {PILL_LABEL}
      </button>
      {expanded && <p className="inline-thinking-text">{text}</p>}
    </div>
  );
});
